import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { BRAND } from '../brand'

/**
 * Catches a render-time throw and shows something, rather than nothing.
 *
 * React unmounts the entire tree when a render throws and no boundary catches
 * it. With no boundary — which is what this app shipped with — the result is a
 * blank white page: no message, no recovery, and nothing for the user to report
 * beyond "it went blank". That is exactly what one did report, and it left
 * nothing to diagnose from.
 *
 * So this is two things at once. It is a real robustness fix, because a plan
 * that trips one component should not take the whole product down. And it is a
 * diagnostic: the error text is rendered on screen, deliberately, so that a
 * screenshot of the failure is a usable bug report. That matters more than it
 * normally would here — the failure only reproduces on a real phone, which means
 * the person who can see it is never the person who can attach a debugger.
 *
 * Two ways out are offered, and the second is the important one. Reloading keeps
 * the URL, so if the plan in the hash is what triggered the throw, reloading
 * walks straight back into it. "Start over" drops the hash first, which is the
 * only escape from a poisoned plan.
 *
 * Must be a class: `getDerivedStateFromError` and `componentDidCatch` have no
 * hook equivalent.
 */

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  componentStack: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Kept in state rather than only logged: on a phone there is no console to
    // open, and the whole point is that the screen itself carries the evidence.
    this.setState({ componentStack: info.componentStack ?? null })
    console.error('Glidepath crashed while rendering:', error, info.componentStack)
  }

  private readonly reload = (): void => {
    window.location.reload()
  }

  private readonly startOver = (): void => {
    // Drop the plan from the URL, THEN reload. If the hash is what broke it, a
    // plain reload would walk straight back into the crash.
    //
    // Two steps, not `location.replace(pathnameWithoutHash)`: a navigation to a
    // URL that differs only by its hash is a same-document navigation and does
    // not reload at all, so that one-liner would clear the address bar and leave
    // this error screen exactly where it was.
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
    window.location.reload()
  }

  override render(): ReactNode {
    const { error, componentStack } = this.state
    if (error === null) return this.props.children

    return (
      <div
        role="alert"
        className="mx-auto flex min-h-[100dvh] w-full max-w-lg flex-col justify-center gap-4 px-4 py-8"
      >
        <h1 className="text-[length:var(--text-title)] text-[color:var(--c-text-primary)]">
          Something broke while drawing your projection.
        </h1>
        <p className="text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]">
          Your amounts were never sent anywhere — every projection runs on your device, and
          that has not changed. This is a bug in {BRAND.name}, not something you did.
        </p>

        {/*
          Shown, not swallowed. A screenshot of this is the bug report, so the
          message has to be legible and selectable rather than tucked behind a
          console nobody on a phone can open.
        */}
        <pre className="overflow-x-auto rounded-[var(--radius-md)] border border-[color:var(--c-border-subtle)] bg-[color:var(--c-bg-surface)] px-3 py-2 text-[length:var(--text-micro)] text-[color:var(--c-negative)]">
          {error.message || String(error)}
          {componentStack === null
            ? ''
            : '\n' + componentStack.split('\n').slice(0, 4).join('\n').trimEnd()}
        </pre>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={this.reload}
            className="rounded-[var(--radius-full)] bg-[color:var(--c-fan-median)] px-4 text-[length:var(--text-label)] text-[color:var(--c-text-on-accent)]"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Try again
          </button>
          <button
            type="button"
            onClick={this.startOver}
            className="rounded-[var(--radius-full)] border border-[color:var(--c-border)] px-4 text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Start over with the defaults
          </button>
        </div>

        <p className="text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]">
          &ldquo;Try again&rdquo; keeps your plan. &ldquo;Start over&rdquo; clears it from
          the address bar — use that one if trying again lands you back here.
        </p>
      </div>
    )
  }
}
